import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CalendarClock, Info } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABEL = {
  ops_payment_link: 'OPS payment link',
  lms_admin: 'LMS admin',
  edd_plus_7: 'EDD + 7 days (prenatal rule)',
  tenure_end: 'Enrolled tenure',
  renewal_date: 'Renewal date',
};

const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

// Keys are `<end date>:<days before>` — the email reminder sent N days before THAT end date.
const ReminderList = ({ reminders, sent, endDate }) => (
  <div className="space-y-0.5" data-testid="membership-expiry-reminders">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Expiry reminder emails sent</p>
    {reminders.map((k) => {
      const [forDate, days] = k.split(':');
      const at = sent?.[k]?.at;
      const earlier = endDate && forDate !== endDate;
      return (
        <p key={k} className="text-[11px] text-muted-foreground" data-testid={`membership-reminder-${k}`}>
          {days}-day reminder{at ? ` · emailed ${new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} · for end date {fmt(forDate)}
          {earlier && <span className="ml-1 text-amber-700">(an earlier end date — no longer applies)</span>}
        </p>
      );
    })}
  </div>
);

export const MembershipExpiryCard = ({ userId }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/admin/users/${userId}/membership`);
      setData(res.data);
      setDraft(res.data?.explicit_course_expiry_date || '');
    } catch { setData(null); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const save = async (value) => {
    setBusy(true);
    try {
      const res = await api.put(`/api/admin/users/${userId}/course-expiry`, { course_expiry_date: value || null, reason: reason || null });
      toast.success(value ? `Access now ends ${fmt(value)}` : 'LMS expiry date cleared');
      if (res.data?.released?.batches_released?.length) toast.info(`Removed from ${res.data.released.batches_released.length} upcoming batch(es)`);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.detailObject?.code || 'Could not update expiry date');
    } finally { setBusy(false); }
  };

  if (!data || !data.is_elite) return null;
  const fromOps = data.explicit_source === 'ops_payment_link';
  const reminders = Object.keys(data.reminders_sent || {});

  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid="membership-expiry-card">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5 shrink-0" />Course expiry
        </p>
        {data.access_blocked ? <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="membership-expiry-blocked">Access ended</Badge>
          : data.prenatal_window_ended
            ? <Badge className="bg-teal-100 text-teal-800 border-teal-200 text-[10px]" data-testid="membership-expiry-prenatal-ended">Prenatal window ended · account active</Badge>
            : data.end_date ? <Badge variant="secondary" className="text-[10px]">Active</Badge>
              : <Badge variant="outline" className="text-[10px]">Open-ended</Badge>}
      </div>
      <p className="text-sm" data-testid="membership-expiry-summary">
        {data.end_date ? <>{data.end_scope === 'prenatal' ? 'Prenatal access ends' : 'Access ends'} <b>{fmt(data.end_date)}</b></> : 'No end date on file'}
        {data.end_source && <span className="text-[11px] text-muted-foreground"> · via {SOURCE_LABEL[data.end_source] || data.end_source}</span>}
      </p>
      {data.end_source === 'edd_plus_7' && (
        <p className="text-[11px] text-muted-foreground">Prenatal Elite ends 7 days after the due date ({fmt(data.edd)}) — only prenatal classes/courses are revoked; the account, Baby Tracker and SoulAI stay active. Set a date below to override.</p>
      )}
      {reminders.length > 0 && <ReminderList reminders={reminders} sent={data.reminders_sent} endDate={data.end_date} />}
      {fromOps && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="membership-expiry-ops-note">
          <Info className="h-3 w-3 shrink-0" /> Set by the OPS payment link. You can change it here; if OPS sends a new date later, that replaces yours.
        </p>
      )}
      <div className="space-y-1.5 pt-1 border-t border-border/60">
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8 text-xs w-full min-w-0" data-testid="membership-expiry-date-input" />
            <Button size="sm" className="h-8 text-xs" disabled={busy || !draft || draft === data.explicit_course_expiry_date}
              onClick={() => save(draft)} data-testid="membership-expiry-save-btn">Save</Button>
            {data.explicit_course_expiry_date && (
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => save(null)} data-testid="membership-expiry-clear-btn">Clear</Button>
            )}
          </div>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-8 text-xs" data-testid="membership-expiry-reason-input" />
          <p className="text-[11px] text-muted-foreground">Access pauses the day after (IST), she gets email reminders 5 and 1 day before, and is removed from upcoming batches once it passes.</p>
      </div>
    </div>
  );
};
